import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from "@angular/material/dialog";
import { UsersService } from "../../../../services/users.service";
import { GeneralItemDetailDialogComponent } from "../../../../shared-components/general-item-detail-dialog/general-item-detail-dialog.component";
import { MatPaginator } from "@angular/material/paginator";
import { MatTableDataSource } from "@angular/material/table";
import { EMPTY, Observable, of } from "rxjs";
import { DialogsService } from "../../../../services/dialogs.service";
import { SnackService } from "../../../../services/snack.service";
import { map, switchMap, tap } from "rxjs/operators";
import { Sort } from "@angular/material/sort";
import { CreateResearcherDialogComponent } from "../create-researcher-dialog/create-researcher-dialog.component";
import { Researcher } from "../../../../model/researcher";
import { Constants } from "../../../../utils/constants";
import { Box } from "../../../../model/box";
import { AddResearchersDialogComponent } from "../../../boxes/add-researchers-dialog/add-researchers-dialog.component";
import { AuthService } from "../../../../services/auth.service";
import { merge } from "lodash-es";

@Component({
  selector: 'aw-list-researchers',
  templateUrl: './list-researchers.component.html',
  styleUrls: ['./list-researchers.component.scss']
})
export class ListResearchersComponent implements OnInit {

  public displayedColumns = [

    'firstName',
    'lastName',
    'email',
    'phone',
    'address',
    'enabled',
    'action'
  ];
  disabled = false;
  disabledItemId: number[] = [];
  loading = false;
  @ViewChild('paginatorResearchers') paginatorUsers!: MatPaginator;
  @Input() box?: Box;
  @Input() embeddedLayout = false;
  dataSource!: MatTableDataSource<Researcher>;
  private allResearchers$!: Observable<Researcher[]>;
  loggedUser: any;
  roles = Constants.roles;
  boxStatuses = Constants.boxStatuses;
  contractStatuses = Constants.contractStatuses;

  constructor(
    public dialog: MatDialog,
    private usersService: UsersService,
    private authService: AuthService,
    private dialogsService: DialogsService,
    private snackService: SnackService
  ) {
  }

  ngOnInit(): void {
    this.loggedUser = this.authService.loggedUser();
  }

  ngAfterViewInit(): void {
    this.allResearchers$ =
      of(true)
        .pipe(
          tap(() => this.loading = true),
          switchMap(() => {
            if (!this.box)
              return this.usersService.getAllResearchers();
            if (this.loggedUser.role !== this.roles.logistic)
              return this.usersService.getAllResearchersByBox(this.box);
            return of([]);
          }),
          map(researchers => { return researchers; }),
          tap(x => {
            this.dataSource = new MatTableDataSource<Researcher>(x);
            this.dataSource.paginator = this.paginatorUsers;
            this.loading = false;
          })
        );
    this.allResearchers$.subscribe();
  }

  applyFilters(event: string): void {
    this.dataSource.filter = event.trim().toLowerCase();
  }

  doSortUsers(event: Sort): void {
    this.dataSource.paginator?.firstPage();
    this.dataSource.data = this.dataSource.data.sort((a, b) => {

      const index: string = event.active;


      if (event.direction === 'asc') {
        return ('' + (b as any)[index]).localeCompare('' + (a as any)[index]);
      } else {
        return ('' + (a as any)[index]).localeCompare('' + (b as any)[index]);
      }


    }
    );
  }

  details(researcher: Researcher): void {
    this.dialog.open(GeneralItemDetailDialogComponent, { data: researcher });
  }

  createUser(): void {
    this.disabled = true;
    this.dialog
      .open(CreateResearcherDialogComponent, { data: new Researcher() })
      .afterClosed()
      .pipe(switchMap(value => value ? this.allResearchers$ : EMPTY))
      .subscribe(
        () => {
          this.disabled = false;
        },
        (error) => {
          this.snackService.error(error.message);
          this.disabled = false;
        }, () => {
          this.disabled = false;
        });
  }

  editUser(researcher: Researcher): void {
    this.disabled = false;
    this.dialog
      .open(CreateResearcherDialogComponent, { data: researcher })
      .afterClosed()
      .pipe(switchMap(value => value ? this.allResearchers$ : EMPTY))
      .subscribe(
        () => {
          this.disabled = false;
        },
        (error) => {
          this.snackService.error(error.message);
          this.disabled = false;
        }, () => {
          this.disabled = false;
        });
  }

  deleteUser(researcher: Researcher): void {
    this.disabled = true;
    const id = researcher.id ?? -1;
    this.disabledItemId.push(id);
    this.dialogsService
      .confirm('Delete researcher', `Are you sure you want to delete the researcher "${researcher.firstName} ${researcher.lastName}"?`)
      .pipe(
        switchMap((value) => value ?
          this.usersService.deleteResearcher(<number>researcher.id) :
          EMPTY),
        switchMap(() => this.allResearchers$)
      )
      .subscribe(() => {
        this.disabled = false;
        this.snackService.success('Researcher deleted!');
        this.disabledItemId.splice(this.disabledItemId.indexOf(id), 1);
      }, (error) => {
        this.snackService.error(error.message);
        this.disabled = false;
        this.disabledItemId.splice(this.disabledItemId.indexOf(id), 1);
      },
        () => {
          this.disabled = false;
          this.disabledItemId.splice(this.disabledItemId.indexOf(id), 1);
        });

  }

  addResearcherToThisBox(): void {
    this.dialog.open(AddResearchersDialogComponent, { data: this.box })
      .afterClosed()
      .pipe(switchMap(value => value ? this.allResearchers$ : EMPTY))
      .subscribe(
        () => {
          this.disabled = false;
        },
        (error) => {
          this.snackService.error(error.message);
          this.disabled = false;
        }, () => {
          this.disabled = false;
        });
  }

  changeResearcherHabilitation(researcher: Researcher): void {
    this.disabled = true;
    const editedResearcher = merge(new Researcher(), researcher);
    editedResearcher.enabled = !researcher.enabled;

    const keyword = editedResearcher.enabled ? 'enable' : 'disable';

    this.dialogsService
      .confirm(`${keyword.toUpperCase()} researcher`, `Are you sure you want to ${keyword} the researcher "${researcher.firstName} ${researcher.lastName}"?`)
      .pipe(
        switchMap((value) => value ? this.usersService.updateResearcher(editedResearcher) : EMPTY),
        switchMap(() => this.allResearchers$)
      )
      .subscribe(() => {
        this.disabled = false;
        this.snackService.success(`Researcher ${keyword}d!`);
      },
        (error) => {
          this.snackService.error(error.message);
          this.disabled = false;
        },
        () => {
          this.disabled = false;
        });
  }

  removeResearcherFromBox(researcher: Researcher): void {
    this.disabled = true;
    this.dialogsService
      .confirm('Remove researcher', `Are you sure you want to remove the researcher "${researcher.firstName} ${researcher.lastName}" from the box "${this.box?.identifier}"?`)
      .pipe(
        switchMap((value) => value ?
          this.usersService.removeResearcherFromBox(<number>this.box?.id, <number>researcher.id) :
          EMPTY),
        switchMap(() => this.allResearchers$)
      )
      .subscribe(() => {
        this.disabled = false;
        this.snackService.success('Researcher removed!');
      }, (error) => {
        this.snackService.error(error.message);
        this.disabled = false;
      },
        () => {
          this.disabled = false;
        });


  }
}
